'use client';

import React, { useState, useRef, useEffect, useMemo } from 'react';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useTekstClient } from 'hooks/useTekstClient';
import styles from './PaginationControls.module.scss';

/**
 * Pagination controls component
 * @param {number} currentPage - Current page number
 * @param {number} totalPages - Total number of pages
 * @param {number} totalItems - Total number of items
 * @param {number} perPage - Items per page
 * @param {number[]} perPageOptions - Options for items per page dropdown
 */
export default function PaginationControls({
    currentPage,
    totalPages,
    totalItems,
    perPage,
    perPageOptions = [3, 6, 12],
}) {
    const { tekst } = useTekstClient();
    const pathname = usePathname();
    const router = useRouter();
    const searchParams = useSearchParams();
    const dropdownId = 'pagination-per-page-menu';

    const buildUrl = (page, perPageValue) => {
        const params = new URLSearchParams(searchParams.toString());
        params.set('page', String(page));
        params.set('per_page', String(perPageValue));
        return `${pathname}?${params.toString()}`;
    };

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const didMountRef = useRef(false);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        const handleEscape = (e) => {
            if (e.key === 'Escape') setDropdownOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    useEffect(() => {
        // Skip initial render; only smooth-scroll when user changes page/per-page.
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentPage, perPage]);

    const handlePerPageSelect = (value) => {
        setDropdownOpen(false);
        router.push(buildUrl(1, value), { scroll: false });
    };

    const options = useMemo(
        () => (perPageOptions.includes(perPage)
            ? perPageOptions
            : [...perPageOptions, perPage].sort((a, b) => a - b)),
        [perPage, perPageOptions]
    );

    if (totalItems === 0) {
        return null;
    }

    const fromItem = (currentPage - 1) * perPage + 1;
    const toItem = Math.min(currentPage * perPage, totalItems);

    return (
        <div className={`${styles.pagination}`}>
            <div className={styles.paginationInner}>
                <span className={styles.paginationText}>
                    {tekst('mkgeu:mkgaddons:pagination:range', {
                        from: fromItem,
                        to: toItem,
                        total: totalItems,
                    })}
                </span>

                <div className={styles.perPageSelector}>
                    <span className={styles.perPageLabel}>{tekst('mkgeu:mkgaddons:pagination:show')}</span>
                    <div className={styles.perPageDropdown} ref={dropdownRef}>
                        <button
                            type="button"
                            className={styles.perPageSelect}
                            onClick={() => setDropdownOpen((prev) => !prev)}
                            aria-haspopup="listbox"
                            aria-expanded={dropdownOpen}
                            aria-controls={dropdownId}
                        >
                            {perPage} <span className={styles.perPageChevron}>&#9662;</span>
                        </button>
                        {dropdownOpen && (
                            <ul id={dropdownId} className={styles.perPageMenu} role="listbox">
                                {options.map((opt) => (
                                    <li key={opt} className={styles.perPageOptionItem}>
                                        <button
                                            type="button"
                                            role="option"
                                            aria-selected={opt === perPage}
                                            className={`${styles.perPageOption} ${opt === perPage ? styles.perPageOptionActive : ''}`}
                                            onClick={() => handlePerPageSelect(opt)}
                                        >
                                            {opt}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <span className={styles.perPageLabel}>{tekst('mkgeu:mkgaddons:pagination:perpage')}</span>
                </div>

                <div className={styles.paginationArrows}>
                    {currentPage > 1 ? (
                        <Link
                            href={buildUrl(currentPage - 1, perPage)}
                            prefetch={false}
                            scroll={false}
                        >
                            &lt;
                        </Link>
                    ) : (
                        <span className={styles.paginationArrowDisabled}>&lt;</span>
                    )}

                    {currentPage < totalPages ? (
                        <Link
                            href={buildUrl(currentPage + 1, perPage)}
                            prefetch={false}
                            scroll={false}
                        >
                            &gt;
                        </Link>
                    ) : (
                        <span className={styles.paginationArrowDisabled}>&gt;</span>
                    )}
                </div>
            </div>
        </div>
    );
}
